import {
  COBROWSER_PERMISSION,
  FilterType,
  IFilter,
  ISenderControlRequestedListener,
  Sender,
} from "@auvious/cobrowser";
import { IRemoteCursorMoved } from "@auvious/cobrowser/dist/domtools/events/cursor";
import { AuviousCommon, Event } from "@auvious/common";
import {
  AuviousRtc,
  IConferenceSession,
  IEndpoint,
  IPublishedStream,
  IPublishOptions,
  IStream,
  StreamTypes,
} from "@auvious/rtc";
import { store } from "@stencil/redux";
import ky from "ky";
import {
  EndpointTypeEnum,
  ErrorCode,
  ICobrowseSenderEventHandlers,
  IEndpointMetadata,
  KEY_COBROWSE_PARTICIPANTS,
  KEY_COBROWSE_SESSION_ID,
  KEY_SKETCH_CONNECTION_ID,
  KEY_COBROWSE_USED_TICKETS,
  UserRoleEnum,
} from "../models";
import {
  CobrowseStateEnum,
  CobrowseTerminateReason,
  KEY_COBROWSE_REQUEST_TYPE,
  KEY_COBROWSE_TICKET,
  KEY_REFRESH_TOKEN,
  UserCapabilityEnum,
} from "../models/enums";
import {
  CobrowseDisplayCaptureAcceptEvent,
  CobrowseDisplayCaptureDenyEvent,
  CobrowseDisplayCaptureRequestEvent,
  CobrowseDisplayCaptureTerminateEvent,
  IEvent,
  SketchAvailabilityChangedEvent,
  SketchToolChangedEvent,
} from "../models/Event";
import { HandledError } from "../models/HandledError";
import {
  dispatchCobrowseMetadataChange,
  dispatchCobrowseOriginChange,
  dispatchCobrowseRemoteEndpointMetadataChange,
  dispatchCobrowseStateChange,
  dispatchCobrowseTicketChange,
  dispatchCobrowseReset,
} from "../store/actions/cobrowse";
import {
  dispatchSketchEnded,
  dispatchSketchRequest,
  dispatchSketchToolChanged,
} from "../store/actions/sketch";
import {
  AppConfigService,
  AppConfigService as Config,
} from "./app.config.service";
import { AVBusService } from "./bus.service";
import { DeviceService } from "./device.service";
import { LocalMediaService } from "./local.media.service";
import { Log } from "./log.service";
import { NotificationService } from "./notification.service";
import { StorageService } from "./storage.service";
import { CommonService } from "./common.service";

import { workerPath } from "./cobrowse.worker.ts?worker";
import { RTCService } from "./rtc.service";
import { AuthService } from "./auth.service";
import { CobrowseRequestType, Restorable } from "@auvious/integrations";

class CobrowseServiceCtrl extends Restorable {
  private sender: Sender;
  private remoteUserController: ISenderControlRequestedListener;
  private session: IConferenceSession = null;
  private rtcClient: AuviousRtc = null;
  private commonClient: AuviousCommon;
  private conferenceId: string;
  private isTerminated = false;
  private handlers: ICobrowseSenderEventHandlers;
  private isPublishingStream = false;
  private screenStream: IPublishedStream;
  private endpointMetadataMap: Map<string, IEndpointMetadata> = new Map();
  private originator: IEndpoint;
  private sketchConnectionId: string;
  private terminateReason: CobrowseTerminateReason;
  private displayCaptureStoppedByUserCb: () => void;

  dispatchCobrowseMetadataChange: typeof dispatchCobrowseMetadataChange;
  dispatchSketchRequest: typeof dispatchSketchRequest;
  dispatchSketchEnded: typeof dispatchSketchEnded;
  dispatchSketchToolChanged: typeof dispatchSketchToolChanged;
  dispatchCobrowseRemoteEndpointMetadataChange: typeof dispatchCobrowseRemoteEndpointMetadataChange;
  dispatchCobrowseStateChange: typeof dispatchCobrowseStateChange;
  dispatchCobrowseTicketChange: typeof dispatchCobrowseTicketChange;
  dispatchCobrowseOriginChange: typeof dispatchCobrowseOriginChange;
  dispatchCobrowseReset: typeof dispatchCobrowseReset;

  constructor() {
    super([
      KEY_COBROWSE_TICKET,
      KEY_COBROWSE_PARTICIPANTS,
      KEY_COBROWSE_SESSION_ID,
      KEY_SKETCH_CONNECTION_ID,
      KEY_COBROWSE_REQUEST_TYPE,
      KEY_COBROWSE_USED_TICKETS,
    ]);
    store.mapDispatchToProps(this, {
      dispatchCobrowseMetadataChange,
      dispatchSketchRequest,
      dispatchSketchEnded,
      dispatchSketchToolChanged,
      dispatchCobrowseStateChange,
      dispatchCobrowseTicketChange,
      dispatchCobrowseOriginChange,
      dispatchCobrowseRemoteEndpointMetadataChange,
      dispatchCobrowseReset,
    });
  }

  /**
   *
   * @param ticket
   * @returns the conferenceId and interactionId associated to that ticket
   */
  async authenticate(
    ticket: string
  ): Promise<{ conferenceId: string; interactionId: string }> {
    try {
      this.commonClient = await CommonService.createInstance();

      const ticketResponse = await AuthService.authenticate(
        ticket,
        this.commonClient,
        this.handleEvent.bind(this)
      );

      if (
        AppConfigService.applicationId &&
        AppConfigService.applicationVerifySource &&
        ticketResponse?.properties?.application_id !==
          AppConfigService.applicationId
      ) {
        throw new HandledError(
          "application-id-mismatch",
          ErrorCode.SECURITY_APPLICATION_ID_MISMATCH
        );
      }

      this.rtcClient = await RTCService.createInstance(this.commonClient);

      this.conferenceId = ticketResponse.properties.conference_id;

      return {
        conferenceId: ticketResponse.properties.conference_id,
        interactionId: ticketResponse.properties.interaction_id,
      };
    } catch (ex) {
      switch (ex.message) {
        case "application-id-mismatch":
          NotificationService.error("Invalid PIN", {
            body: "The PIN is not from a trusted source.",
            errorCode: ErrorCode.SECURITY_APPLICATION_ID_MISMATCH,
          });
          break;
        case "404":
          NotificationService.error("Invalid PIN", {
            body: "Please check for typos or case-sensitive characters.",
            errorCode: ErrorCode.COBROWSE_INVALID_PIN,
          });
          break;
        case "401":
          NotificationService.error("PIN expired", {
            body: "It appears the PIN has already been used.",
          });
          break;
      }
      throw ex;
    }
  }

  /*
  async authenticate(
    ticket: string
  ): Promise<{ conferenceId: string; interactionId: string }> {
    try {
      this.commonClient = await CommonService.createInstance();

      const ticketResponse: ITicketRequest = (await ky
        .get(`${Config.auviousBaseUrl}/security/ticket/${ticket}`)
        .json()) as any;

      this.eventService = new EventClient(this.commonClient);
      this.eventService.connect().subscribe((e) => this.handleEvent(e));

      let refreshToken = this.getRefreshToken(ticket);

      const params = new URLSearchParams();
      params.set("client_id", "customer");

      if (refreshToken) {
        params.set("grant_type", "refresh_token");
        params.set("refresh_token", refreshToken);
      } else {
        params.set("grant_type", "password");
        params.set("username", ticket);
      }

      this.authToken = await this.commonClient.loginTemplate(
        () =>
          ky
            .post(`${Config.auviousBaseUrl}/security/oauth/token`, {
              body: params,
            })
            .json(),
        async (refreshToken) => {
          const refreshTokenParams = new URLSearchParams();
          refreshTokenParams.set("client_id", params.get("client_id"));
          refreshTokenParams.set("grant_type", "refresh_token");
          refreshTokenParams.set("refresh_token", refreshToken);

          const token = await (
            await ky.post(`${Config.auviousBaseUrl}/security/oauth/token`, {
              body: refreshTokenParams,
            })
          ).json();

          StorageService.setItem(
            `${KEY_REFRESH_TOKEN}.${ticket}`,
            token.refresh_token,
            "session"
          );

          return token;
        }
      );

      Log.info("cobrowse: token: ", this.authToken);

      StorageService.setItem(
        `${KEY_REFRESH_TOKEN}.${ticket}`,
        this.authToken.refreshToken,
        "session"
      );

      this.rtcClient = await RTCService.createInstance(this.commonClient);

      Log.info("cobrowse: ticket response: ", ticketResponse);
      this.conferenceId = ticketResponse.properties.conference_id;
      return {
        conferenceId: ticketResponse.properties.conference_id,
        interactionId: ticketResponse.properties.interaction_id,
      };
    } catch (ex) {
      switch (ex.message) {
        case "404":
          NotificationService.error("Invalid PIN", {
            body: "Please check for typos or case-sensitive characters.",
            errorCode: ErrorCode.COBROWSE_INVALID_PIN,
          });
          break;
        case "401":
          NotificationService.error("PIN expired", {
            body: "It appears the PIN has already been used.",
          });
          break;
      }
      throw ex;
    }
  }

  private getRefreshToken(ticket: string) {
    return StorageService.getItem(
      `${KEY_REFRESH_TOKEN}.${ticket}`,
      "session"
    );
  }

  */

  private handleEvent(event: Event) {
    switch (event.payload?.type) {
      case SketchAvailabilityChangedEvent.type:
        (event.payload as SketchAvailabilityChangedEvent).available
          ? this.startSketch(
              (event.payload as SketchAvailabilityChangedEvent).connectionId
            )
          : this.stopSketch();
        break;
      case SketchToolChangedEvent.type:
        this.dispatchSketchToolChanged(
          (event.payload as SketchToolChangedEvent).tool
        );
        break;
      case CobrowseDisplayCaptureRequestEvent.type:
        this.captureDisplay(true);
        break;
      case CobrowseDisplayCaptureTerminateEvent.type:
        this.closeScreenStream();
        break;
    }
  }

  public captureDisplay(confirm: boolean) {
    this.handlers?.displayCaptureRequested(confirm);
  }

  /**
   *
   * @param userId the user id, aka username or 'sub' claim
   * @param userEndpointId the user endpoint id, use null to send to all active user endpoints
   * @param event the event conforming to interface IEvent
   */
  public async sendEventMessage(
    userId: string,
    userEndpointId: string,
    event: IEvent
  ) {
    const body = { userId, userEndpointId, event };
    const messagesSent = await ky
      .post(`${Config.auviousBaseUrl}/rtc-api/users/endpoints/sendEvent`, {
        json: body,
        headers: {
          Authorization: `Bearer ${AuthService.accessToken}`,
        },
      })
      .json();
    return messagesSent;
  }

  private tryResumeSketch() {
    const connectionId = StorageService.getItem(
      KEY_SKETCH_CONNECTION_ID,
      "session"
    );
    if (!connectionId) {
      return;
    }
    this.sketchConnectionId = connectionId;
    this.dispatchSketchRequest(connectionId);
  }

  private startSketch(connectionId) {
    this.sketchConnectionId = connectionId;
    this.dispatchSketchRequest(connectionId);
    StorageService.setItem(KEY_SKETCH_CONNECTION_ID, connectionId, "session");
  }

  public stopSketch() {
    this.sketchConnectionId = undefined;
    this.dispatchSketchEnded();
    StorageService.removeItem(KEY_SKETCH_CONNECTION_ID, "session");
  }

  public async startDisplayCapture() {
    // notify that we accepted
    this.sendEventMessage(
      this.remoteUserController.remoteId,
      this.remoteUserController.remoteEndpointId,
      new CobrowseDisplayCaptureAcceptEvent()
    );

    try {
      const stream = await LocalMediaService.openDisplayStream({
        video: { frameRate: 10 },
      });

      this.listenToStreamEvents(stream);

      await this.publishStream(stream);
    } catch (ex) {
      let message = ex.message;
      let alert = true;
      let log = false;

      switch (ex?.name) {
        case "NotAllowedError":
          // same for chrome / edge
          if (message === "Permission denied by system") {
            message =
              "Please enable screen recording in your System Preferences";
          } else if (message === "Permission denied") {
            alert = false;
          }

          break;
        case "NotFoundError":
          // happens in Firefox / MacOS Catalina when the used hasn't given access in System Prerefences
          message = "Please enable screen recording in your System Preferences";
          break;
        case "InvalidStateError":
          // firefox: getDisplayMedia must be called from a user gesture handler.
          alert = false;
          log = true;
          break;
        default:
          log = true;
      }

      this.handlers?.displayCaptureRejected?.(ex?.name);

      if (alert) {
        NotificationService.info(message);
      }

      if (log) {
        AVBusService.errorLog(
          new HandledError(ex, ErrorCode.COBROWSE_DISPLAY_CAPTURE_FAILED)
        );
      }
    }
  }

  public stopDisplayCapture() {
    this.closeScreenStream();
  }

  public denyDisplayCapture() {
    this.sendEventMessage(
      this.remoteUserController.remoteId,
      this.remoteUserController.remoteEndpointId,
      new CobrowseDisplayCaptureDenyEvent()
    );
  }

  private listenToStreamEvents(stream: MediaStream) {
    stream.getVideoTracks()[0].addEventListener("ended", () => {
      // stream closed by user
      this.closeScreenStream();
      this.displayCaptureStoppedByUserCb?.();
    });
  }

  public listenDisplayCaptureEndedByUser(fn) {
    this.displayCaptureStoppedByUserCb = fn;
  }

  private async publishStream(mediaStream: MediaStream) {
    try {
      if (
        !mediaStream ||
        this.isPublishingStream ||
        !this.session?.isEstablished()
      ) {
        return;
      }

      this.isPublishingStream = true;

      const streamToPublish: IPublishOptions = {
        mediaStream,
        type: StreamTypes.SCREEN,
        metadata: LocalMediaService.getStreamMetadata(mediaStream),
      };

      await this.session.publish(streamToPublish);
    } catch (ex) {
      NotificationService.error("Could not publish stream.", {
        errorCode: ErrorCode.COBROWSE_DISPLAY_CAPTURE_PUBLISH_FAILED,
      });
    }
  }

  private unpublishStream(stream: IPublishedStream) {
    if (stream && this.session?.isEstablished()) {
      this.session.unpublish(stream);
    }
  }

  private closeScreenStream() {
    LocalMediaService.closeMediaStream(this.screenStream?.mediaStream);
    this.unpublishStream(this.screenStream);
    this.screenStream = null;
  }

  public isAuthenticated(): boolean {
    return !!this.conferenceId;
  }

  public getMe(): IEndpoint {
    return {
      username: this.rtcClient?.identity(),
      endpoint: this.rtcClient?.endpoint(),
    };
  }

  public get common(): AuviousCommon {
    return this.commonClient;
  }

  async connect(
    handlers: ICobrowseSenderEventHandlers,
    filters: IFilter[] = [],
    customerDisplayName = "",
    requestType: CobrowseRequestType = "view"
  ): Promise<void> {
    try {
      if (!this.conferenceId) {
        throw new Error("Co-browse conferenceId required. Authenticate first.");
      }

      this.handlers = handlers;
      this.isTerminated = false;
      this.terminateReason = undefined;

      if (requestType === "view") {
        this.setupSender(filters, customerDisplayName);
      }

      let endpointType: EndpointTypeEnum;
      switch (requestType) {
        case "display-capture":
          endpointType = EndpointTypeEnum.displayCapture;
          break;
        case "view":
          endpointType = EndpointTypeEnum.coBrowse;
      }

      await this.joinConference(
        this.conferenceId,
        customerDisplayName,
        endpointType
      );

      return;
    } catch (ex) {
      throw ex;
    }
  }

  private setupSender(filters: IFilter[], name: string) {
    this.sender = new Sender(
      // @ts-ignore
      this.commonClient,
      {
        webworkerPath: workerPath,
        userId: this.rtcClient.identity(),
        userEndpointId: this.rtcClient.endpoint(),
        userMetadata: {
          role: UserRoleEnum.customer,
          name,
        },
        filters: [
          ...filters,
          { selector: "app-auvious-widget", type: FilterType.NoDisplay },
        ],
        allowOrigins: Config.allowedOrigins,
        iframeOriginsFilter: Config.cobrowseIFrameOriginFilters,
      },
      {
        sessionCreated: (args: ISenderControlRequestedListener) => {
          // todo: change
          console.log(args);
        },
        terminated: () => {
          Log.info("cobrowse:terminated");
          this.terminated();
        },
        option: (ev) => {
          Log.info("cobrowse:option", ev);
        },
        viewRequested: (ev) => {
          Log.info("cobrowse:view-requested", ev);
          this.remoteUserController = ev;
          this.sender.enable("scroll");
          this.handlers.viewRequested?.();
        },
        controlRequested: (ev) => {
          Log.info("cobrowse:control-requested", ev);
          this.remoteUserController = ev;
          this.handlers.controlRequested?.();
        },
        mousemove: (ev: IRemoteCursorMoved) => {
          this.handlers.mouseMove?.({ top: ev.top, left: ev.left });
        },
      }
    );
  }

  private joinConference(
    conferenceId: string,
    name: string,
    type: EndpointTypeEnum = EndpointTypeEnum.coBrowse
  ): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const capabilities: UserCapabilityEnum[] = [UserCapabilityEnum.coBrowse];
      if (DeviceService.supportsDisplayCapture) {
        capabilities.push(UserCapabilityEnum.displayCapture);
      }
      try {
        const metadata: IEndpointMetadata = {
          type,
          name,
          capabilities,
          cobrowseOriginator: this.originator,
          originUrl: window.location.origin + window.location.pathname,
          customerMetadata:
            !!AppConfigService.customerMetadata &&
            Object.keys(AppConfigService.customerMetadata).length > 0
              ? AppConfigService.customerMetadata
              : null,
        };
        await this.rtcClient.joinConference({
          id: conferenceId,
          eventHandlers: {
            accepted: async (session: IConferenceSession) => {
              this.session = session;
              this.setPreviousSelf();
              const lastSession = StorageService.getItem(
                KEY_COBROWSE_SESSION_ID,
                "session"
              );
              if (lastSession) {
                try {
                  await this.sender.reconnect(lastSession);
                  this.remoteUserController = {
                    ...this.remoteUserController,
                    sessionId: lastSession,
                    timestamp: new Date().toString(),
                  };

                  this.resumed();
                } catch (ex) {
                  AVBusService.errorLog(
                    new HandledError(ex, ErrorCode.COBROWSE_RESUME_FAILED)
                  );
                  this.leave();
                  reject(new Error("Could not resume co-browse session"));
                }
              }
              Log.info("cobrowse:joinConference:accepted");
              resolve();
            },
            endpointJoined: (originator: IEndpoint) => {
              Log.info("cobrowse:joinConference:endpointJoined", originator);
              // store metadata to retrieve on othere events
              this.endpointMetadataMap.set(
                originator.endpoint,
                originator.metadata
              );

              if (this.isAgent(originator.endpoint)) {
                this.dispatchCobrowseRemoteEndpointMetadataChange(
                  originator.metadata
                );
                // update remoteUserController in case the agent has refreshed the page and a new endpoint has joined
                this.remoteUserController = {
                  ...this.remoteUserController,
                  remoteId: originator.username,
                  remoteEndpointId: originator.endpoint,
                };
              }
            },
            endpointGotSick: (originator: IEndpoint) => {
              Log.info("cobrowse:joinConference:endpointGotSick", originator);
              if (
                !originator ||
                this.isPreviousSelf(originator.endpoint) ||
                !this.isAgent(originator.endpoint) ||
                this.isPreviousAgent(originator.endpoint)
              ) {
                return;
              }
              this.handlers.agentGotSick?.();
            },
            endpointRecovered: (originator: IEndpoint) => {
              Log.info("cobrowse:joinConference:endpointRecovered", originator);
              if (
                !originator ||
                this.isPreviousSelf(originator.endpoint) ||
                !this.isAgent(originator.endpoint) ||
                this.isPreviousAgent(originator.endpoint)
              ) {
                return;
              }
              this.handlers.agentRecovered?.();
            },
            endpointLeft: (originator: IEndpoint) => {
              Log.info("cobrowse:joinConference:endpointLeft", originator);
              if (
                !originator ||
                this.isPreviousSelf(originator.endpoint) ||
                !this.isAgent(originator.endpoint) ||
                this.isPreviousAgent(originator.endpoint)
              ) {
                return;
              }
              this.handlers.agentLeft?.();
            },
            streamPublished: (stream: IPublishedStream) => {
              if (stream.type === "screen") {
                this.isPublishingStream = false;
                this.screenStream = stream;
                this.handlers?.displayCaptureLocalStreamPublished?.(stream);
              }
            },
            streamAdded: (stream: IStream) => {
              if (stream.type === "screen") {
                if (stream.originator.endpoint === this.rtcClient.endpoint()) {
                  // my stream
                } else {
                  this.handlers?.displayCaptureRemoteStreamAdded?.(stream);
                }
              }
            },
            streamRemoved: (stream: IStream) => {
              if (stream.type === "screen") {
                this.screenStream = null;
                if (stream.originator.endpoint === this.rtcClient.endpoint()) {
                  this.handlers?.displayCaptureLocalStreamRemoved?.(stream);
                } else {
                  this.handlers?.displayCaptureRemoteStreamRemoved?.(stream);
                }
              }
            },
            failed: (error) => {
              this.session = null;
              this.sender?.cleanup();
              this.sender = null;
              this.conferenceId = null;
              reject(error);
            },
            ended: () => {
              Log.info("cobrowse:joinConference:ended");
              this.session = null;
              this.conferenceId = null;

              // in case we don't get a cobrowse.terminated event
              this.terminated();
            },
            // conferenceMetadataSet: (event: IConferenceMetadataUpdatedEvent) => {
            //     const meta = ConferenceMetadataFactory.fromEvent(event);
            //     if (meta instanceof SketchMetadata) {
            //         // this.dispatchSketchEnded
            //     }
            //     // this.dispatchCobrowseMetadataChange(meta);
            //     // if (meta) {
            //     //     switch (meta.key) {
            //     //         case ConferenceMetadataKeyEnum.sketch:
            //     //             const smeta = meta as SketchMetadata;
            //     //             const isOn = smeta.isEnabled(smeta.id);
            //     //             isOn
            //     //                 ? this.dispatchSketchRequest(smeta.id)
            //     //                 : this.dispatchSketchEnded(smeta.id)
            //     //             break;
            //     //     }
            //     // }
            // },
            // conferenceMetadataRemoved: (event: IConferenceMetadataUpdatedEvent) => {
            //     switch (event.key) {
            //         case ConferenceMetadataKeyEnum.sketch:
            //             this.dispatchSketchEnded('')
            //             break;
            //     }
            // }
          },
          metadata,
        });
      } catch (ex) {
        AVBusService.errorLog(
          new HandledError(ex, ErrorCode.COBROWSE_JOIN_FAILED)
        );
        reject(ex);
      }
    });
  }

  private resumed() {
    const user = Object.values(this.sender.users.getUsers()).pop();

    if (user) {
      this.remoteUserController = {
        ...this.remoteUserController,
        remoteEndpointId: Object.keys(user.endpoints).pop(),
        remoteId: user.id,
      };
    }

    Log.info("session was resumed", this.sender);

    // todo: quick fix for resume not having users map
    this.handlers.resumed?.(
      user?.permissions || { view: true, control: false }
    );

    this.tryResumeSketch();
  }

  async allowViewAccess() {
    await this.allowAccess(COBROWSER_PERMISSION.VIEW);
    StorageService.setItem(
      KEY_COBROWSE_SESSION_ID,
      this.remoteUserController?.sessionId,
      "session"
    );
  }

  async denyViewAccess() {
    this.terminateReason = "user-denied-access";
    await this.denyAccess(COBROWSER_PERMISSION.VIEW);
    this.session.leave();
  }

  allowControlAccess() {
    this.allowAccess(COBROWSER_PERMISSION.CONTROL);
  }

  revokeControlAccess() {
    this.revokeAccess(COBROWSER_PERMISSION.CONTROL);
  }

  denyControlAccess() {
    this.denyAccess(COBROWSER_PERMISSION.CONTROL);
  }

  async terminate(reason: CobrowseTerminateReason) {
    try {
      this.terminateReason = reason;
      if (this.sketchConnectionId) {
        CobrowseService.stopSketch();
      }
      await CobrowseService.leave();
    } catch (ex) {
      AVBusService.errorLog(
        new HandledError(ex, ErrorCode.COBROWSE_LEAVE_FAILED)
      );
    }
  }

  async leave() {
    if (!!this.screenStream) {
      this.stopDisplayCapture();
    }
    this.dispatchCobrowseStateChange(CobrowseStateEnum.terminating);
    try {
      if (!!this.sender) {
        await this.sender.terminate();
        this.terminated();
      } else {
        this.getRequestType() === "display-capture"
          ? this.session.leave("widgetScreenShareTerminated")
          : this.session.leave("widgetCobrowseTerminated");

        this.handlers.terminated?.();
        this.reset();
        this.isTerminated = true;
      }
      this.conferenceId = null;
    } catch (ex) {
      NotificationService.error("Co-browse", {
        body: ex.message,
        errorCode: ErrorCode.COBROWSE_TERMINATE_FAILED,
      });

      this.terminated();
    }
  }

  private terminated() {
    if (this.isTerminated) {
      return;
    }

    this.isTerminated = true;

    try {
      if (this.screenStream) {
        this.stopDisplayCapture();
      }

      if (this.sender?.isActive()) {
        this.sender.terminate();
      }

      this.session?.leave();
    } catch (ex) {
      Log.error("terminated", ex);
    } finally {
      this.handlers.terminated?.();
      this.reset();
    }
  }

  private allowAccess(permission: COBROWSER_PERMISSION) {
    return this.sender.allowAccess(permission, {
      userId: this.remoteUserController.remoteId,
      userEndpointId: this.remoteUserController.remoteEndpointId,
    });
  }

  private revokeAccess(permission: COBROWSER_PERMISSION) {
    return this.sender.revokeAccess(permission, {
      userId: this.remoteUserController.remoteId,
      userEndpointId: this.remoteUserController.remoteEndpointId,
    });
  }

  private denyAccess(permission: COBROWSER_PERMISSION) {
    return this.sender.denyAccess(permission, {
      userId: this.remoteUserController.remoteId,
      userEndpointId: this.remoteUserController.remoteEndpointId,
    });
  }

  private setPreviousSelf() {
    let existingMe: string[];
    try {
      existingMe =
        JSON.parse(
          StorageService.getItem(KEY_COBROWSE_PARTICIPANTS, "session")
        ) || [];
    } catch (ex) {
      existingMe = [];
    }
    existingMe.push(this.rtcClient.endpoint());
    StorageService.setItem(
      KEY_COBROWSE_PARTICIPANTS,
      JSON.stringify(existingMe),
      "session"
    );
  }

  private isPreviousSelf(endpoint: string): boolean {
    if (endpoint === this.rtcClient?.endpoint()) {
      return false;
    }
    let existingMe: string[] = [];
    try {
      existingMe =
        JSON.parse(
          StorageService.getItem(KEY_COBROWSE_PARTICIPANTS, "session")
        ) || [];
    } catch (ex) {
      existingMe = [];
    }
    return existingMe.includes(endpoint);
  }

  private isPreviousAgent(endpoint: string) {
    return this.remoteUserController?.remoteEndpointId !== endpoint;
  }

  private isAgent(endpoint: string): boolean {
    const metadata = this.endpointMetadataMap.get(endpoint);
    if (!metadata) {
      return false;
    }
    return (
      metadata.roles?.includes(UserRoleEnum.agent) &&
      !metadata.roles?.includes(UserRoleEnum.guestAgent)
    );
  }

  public setUsedTicket(ticket: string): void {
    StorageService.setUsedTicket(KEY_COBROWSE_USED_TICKETS, ticket);
  }

  public clearUsedTickets(): void {
    StorageService.clearUsedTickets(KEY_COBROWSE_USED_TICKETS);
  }

  public isTicketUsed(ticket: string): boolean {
    return StorageService.isTicketUsed(KEY_COBROWSE_USED_TICKETS, ticket);
  }

  public setTicket(ticket: string): void {
    StorageService.setItem(KEY_COBROWSE_TICKET, ticket, "session");
  }

  public clearTicket() {
    StorageService.removeItem(KEY_COBROWSE_TICKET, "session");
    this.dispatchCobrowseTicketChange(undefined);
  }

  public getStoredTicket() {
    return StorageService.getItem(KEY_COBROWSE_TICKET, "session");
  }

  public setRequestType(type: CobrowseRequestType) {
    StorageService.setItem(KEY_COBROWSE_REQUEST_TYPE, type, "session");
  }

  public getTerminateReason(): CobrowseTerminateReason {
    return this.terminateReason || "agent-terminated";
  }

  public getRequestType(): CobrowseRequestType {
    return StorageService.getItem(
      KEY_COBROWSE_REQUEST_TYPE,
      "session"
    ) as CobrowseRequestType;
  }

  public clearSessionStorage(): void {
    const ticket = StorageService.getItem(KEY_COBROWSE_TICKET, "session");
    StorageService.removeItem(KEY_COBROWSE_TICKET, "session");
    StorageService.removeItem(`${KEY_REFRESH_TOKEN}.${ticket}`, "session");
    StorageService.removeItem(KEY_COBROWSE_PARTICIPANTS, "session");
    StorageService.removeItem(KEY_COBROWSE_SESSION_ID, "session");
    StorageService.removeItem(KEY_SKETCH_CONNECTION_ID, "session");
    StorageService.removeItem(KEY_COBROWSE_REQUEST_TYPE, "session");
  }

  public getRestorableState() {
    const ticket = StorageService.getItem(KEY_COBROWSE_TICKET, "session");
    const key = [KEY_REFRESH_TOKEN, ticket].join(".");
    if (ticket && !(ticket in this.sessionStoreKeys)) {
      this.sessionStoreKeys.push(key);
    }
    return super.getRestorableState();
  }

  public restoreState(state: object) {
    super.restoreState(state);
    const ticket = state[KEY_COBROWSE_TICKET];
    if (ticket) {
      const key = [KEY_REFRESH_TOKEN, ticket].join(".");
      StorageService.setItem(key, state[key], "session");
    }
  }

  public resetStore(): void {
    this.dispatchCobrowseReset();
  }

  private async reset() {
    await RTCService.logout(this.rtcClient);
    this.rtcClient = null;

    CommonService.logout(this.commonClient);
    this.commonClient = null;

    AuthService.destroy();

    this.sender?.cleanup();
    this.sender = null;
    this.remoteUserController = null;
    this.conferenceId = null;
    this.endpointMetadataMap.clear();
    this.originator = null;

    this.resetStore();
    this.clearSessionStorage();
    this.clearTicket();
  }

  public setOriginator(originator: IEndpoint) {
    this.originator = originator;
  }
}

export const CobrowseService = new CobrowseServiceCtrl();
